/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {firstValueFrom, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';
import template from './tier-priority-modal.component.html';
import {deferMarkForCheck} from '../../common/util/defer-mark-for-check';
import {TierApiService} from './tier-api.service';
import {NetworkApiService} from '../network/network-api.service';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {AppSharedModule} from '../../common/app-shared.module';

@Component({
  standalone: true,
  imports: [AppSharedModule, FormsModule, TranslateModule],
  selector: 'adn-tier-priority-modal',
  template
})
export class TierPriorityModalComponent implements OnInit {
  /** Set via BsModalService initialState */
  inputNetwork: any;

  network: any;
  tiers: any[] = [];
  resolved = false;
  hasDisabledTiers = false;
  pending = false;
  validationError = false;

  readonly onSaved = new Subject<any>();

  constructor(
    public bsModalRef: BsModalRef,
    private readonly tierApi: TierApiService,
    private readonly networkApi: NetworkApiService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Clone so we only send tiers to the API, not the full network object
    this.network = {id: this.inputNetwork.id, tiers: _.cloneDeep(this.inputNetwork.tiers)};
    firstValueFrom(this.tierApi.listAll()).then((page: any) => {
      deferMarkForCheck(this.cdr, () => {
        this.tiers = page.results;
        const indexedNetworkTiers = _.keyBy(this.network.tiers, 'id');
        _.forEach(this.tiers, (tier) => {
          tier.enabled = !!indexedNetworkTiers[tier.id];
          const idx = _.findIndex(this.network.tiers, ['id', tier.id]);
          tier.pos = tier.enabled ? (idx + 1) : 999;
        });
        this.refreshActiveNetworkData();
        this.resolved = true;
      });
    });
  }

  get enabledTiers(): any[] {
    return _.orderBy(this.tiers.filter(t => t.enabled), 'pos');
  }

  get disabledTiers(): any[] {
    return _.orderBy(this.tiers.filter(t => !t.enabled), 'pos');
  }

  toggleEnable(tier: any): void {
    if (tier.enabled) {
      this.network.tiers.push(tier);
    } else {
      tier.pos = 999;
      this.network.tiers = _.reject(this.network.tiers, ['id', tier.id]);
    }
    this.refreshActiveNetworkData();
  }

  priority(idx: number, raise: boolean): void {
    const iLength = this.refreshActiveNetworkData();
    for (let i = 0; i < iLength; i++) {
      const tier = this.network.tiers[i];
      tier.pos = (i + 1) * 10;
      if (i === idx && raise) {
        tier.pos -= 11;
      } else if (i === idx && !raise) {
        tier.pos += 11;
      }
    }
    this.network.tiers = _.orderBy(this.network.tiers, 'pos', 'asc');
    const indexedNetworkTiers = _.keyBy(this.network.tiers, 'id');
    _.forEach(this.tiers, (tier) => {
      const networkTier = indexedNetworkTiers[tier.id];
      tier.pos = networkTier ? networkTier.pos : 999;
    });
  }

  save(): void {
    this.pending = true;
    this.validationError = false;
    firstValueFrom(this.networkApi.save(this.network)).then((updatedNetwork: any) => {
      this.onSaved.next(updatedNetwork);
      this.bsModalRef.hide();
    }, () => {
      deferMarkForCheck(this.cdr, () => {
        this.validationError = true;
        this.pending = false;
      });
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }

  private refreshActiveNetworkData(): number {
    const tierIds = _.map(this.tiers, 'id');
    this.hasDisabledTiers = !!_.find(this.tiers, ['enabled', false]);
    const activeNetworkTierLength = _.intersection(tierIds, _.map(this.network.tiers, 'id')).length;
    if (activeNetworkTierLength !== tierIds.length) {
      this.network.tiers = _.filter(this.network.tiers, (t: any) => tierIds.indexOf(t.id) > -1);
    }
    return activeNetworkTierLength;
  }
}
